import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { catchError, EMPTY, tap } from 'rxjs';
import { CustomerAccount, CustomerApplication } from './customer-account';
import { CustomerAccountService } from './customer-account.service';
import { RememberedOrgKeyStorage } from './remembered-org-key';
import { BillingParty } from './signup';

interface CustomerAccountState {
  /** The signed-in account's applications; empty until listed, and when the list is unavailable. */
  applications: CustomerApplication[];
  orgKey: string | undefined;
  account: CustomerAccount | undefined;
  loading: boolean;
  /** True once a load has failed or answered nothing, so the shell can offer the key prompt. */
  notFound: boolean;
  saving: boolean;
}

const initialState: CustomerAccountState = {
  applications: [],
  orgKey: undefined,
  account: undefined,
  loading: false,
  notFound: false,
  saving: false,
};

/**
 * The customer portal's state: which organization, its account, and the two writes.
 *
 * **`orgKey` is state, not a route parameter.** The portal is mounted at `/portal` with no key in
 * the URL, because there is no principal to derive one from and putting it in the path would invite
 * a customer to share a link that reads their invoices. It comes from
 * {@link RememberedOrgKeyStorage} — written at the end of a successful sign-up — and from the prompt
 * when nothing is remembered.
 *
 * **`notFound` is a state rather than an error.** A remembered key can be wrong, and the portal's
 * answer is to ask for another one rather than to show a failure; it is also what a disabled portal
 * looks like, since the flag makes every `/customers/**` path a 404. Those two are indistinguishable
 * by design and the portal does not try.
 *
 * **The account's own applications come first.** One person is one login, and a second subscription
 * is a second organization under the same account. The list is read from the token, so with one
 * application the portal opens it, with several it lets the customer choose, and only with none — a
 * gateway that cannot list, or a customer who has not joined anything yet — does it fall back to the
 * remembered key and the prompt.
 *
 * **After a write, the whole account is reloaded** rather than patched from the response. The two
 * writes answer only the thing they changed — a `BillingParty`, a `Subscription` — and the status
 * card renders the plan and the period alongside them. Patching one field would leave a card that is
 * right about the cancellation and stale about everything else.
 */
export const CustomerAccountStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    subscription: computed(() => store.account()?.subscription),
    plan: computed(() => store.account()?.plan),
    billingParty: computed(() => store.account()?.billingParty),
    invoices: computed(() => store.account()?.invoices ?? []),

    /**
     * A tenant bootstrapped rather than bought: a real state, and a different thing to say than
     * "we could not load your subscription".
     */
    hasNoSubscription: computed(() => !!store.account() && !store.account()?.subscription),

    /** Whether the portal has a key to work with at all; false means show the prompt. */
    hasOrgKey: computed(() => !!store.orgKey()),

    /** Several applications and none chosen yet: the "Your applications" cards. */
    choosingApplication: computed(() => store.applications().length > 1 && !store.orgKey()),

    /** Whether "Switch application" has anywhere to go. */
    canSwitchApplication: computed(() => store.applications().length > 1),
  })),
  withMethods((store) => {
    const service = inject(CustomerAccountService);
    const remembered = inject(RememberedOrgKeyStorage);

    function load(orgKey: string): void {
      patchState(store, { loading: true, notFound: false });
      service
        .findByOrgKey(orgKey)
        .pipe(
              tap((account) => {
                remembered.write(orgKey);
                patchState(store, { account, loading: false, notFound: false });
              }),
              catchError(() => {
                // The interceptor has already said what went wrong. What this adds is the portal's own
                // next move: offer the key prompt, because the commonest cause is a key that is not
                // this customer's.
                remembered.clear();
                patchState(store, { orgKey: undefined, account: undefined, loading: false, notFound: true });
                return EMPTY;
              }),
        )
        .subscribe();
    }

    /** The pre-list behaviour: the remembered key, or the prompt when there is none. */
    function openRemembered(): void {
      const orgKey = remembered.read();
      if (!orgKey) {
        patchState(store, { orgKey: undefined, account: undefined, notFound: false });
        return;
      }
      patchState(store, { orgKey });
      load(orgKey);
    }

    return {
      /**
       * Opens the portal on the account's applications, or on whatever key is available.
       *
       * Called by the shell on construction. One application is opened directly; several wait for the
       * customer's choice, unless the remembered key is one of them. With none it settles into the
       * remembered key or "no key", which the shell renders as the prompt — not as an error, because
       * a customer who has never signed up on this browser has done nothing wrong.
       */
      initialise(): void {
        patchState(store, { loading: true });
        service
          .listApplications()
          .pipe(
            tap((applications) => {
              patchState(store, { applications, loading: false });
              const rememberedKey = remembered.read();
              if (applications.length === 1) {
                patchState(store, { orgKey: applications[0].orgKey });
                load(applications[0].orgKey);
              } else if (applications.length > 1) {
                const known = applications.find((application) => application.orgKey === rememberedKey);
                patchState(store, { orgKey: known?.orgKey, account: undefined, notFound: false });
                if (known) load(known.orgKey);
              } else {
                openRemembered();
              }
            }),
            catchError(() => {
              // A gateway without the list is the portal as it was: remembered key, then the prompt.
              patchState(store, { applications: [], loading: false });
              openRemembered();
              return EMPTY;
            }),
          )
          .subscribe();
      },

      /** "Manage" on one of the cards: the tabs below are about that application from now on. */
      selectApplication(orgKey: string): void {
        patchState(store, { orgKey });
        load(orgKey);
      },

      /** Back to the cards. */
      switchApplication(): void {
        patchState(store, { orgKey: undefined, account: undefined, notFound: false });
      },

      /**
       * Opens the portal on a key the customer typed.
       *
       * It is remembered only after a successful load. A rejected key must not send the customer
       * back to the same failing request when they reload the portal.
       */
      openOrganization(orgKey: string): void {
        patchState(store, { orgKey });
        load(orgKey);
      },

      clearNotFound(): void {
        patchState(store, { notFound: false });
      },

      /** Drops the remembered key and returns to the prompt. The portal's "not my organization". */
      forgetOrganization(): void {
        remembered.clear();
        patchState(store, { ...initialState, applications: store.applications() });
      },

      reload(): void {
        const orgKey = store.orgKey();
        if (orgKey) load(orgKey);
      },

      /**
       * Saves a corrected invoiced party, then reloads.
       *
       * @returns nothing; the caller watches `saving()` for the button state, and the success
       *   snackbar is the component's to raise
       */
      saveBillingParty(billingParty: BillingParty, onSaved?: () => void): void {
        const orgKey = store.orgKey();
        if (!orgKey || store.saving()) return;
        patchState(store, { saving: true });
        service
          .updateBillingParty(orgKey, billingParty)
          .pipe(
            tap(() => {
              patchState(store, { saving: false });
              load(orgKey);
              onSaved?.();
            }),
            catchError(() => {
              patchState(store, { saving: false });
              return EMPTY;
            }),
          )
          .subscribe();
      },

      /**
       * Cancels at period end, then reloads.
       *
       * The reload is what makes the status card say "cancels on {date}" rather than "cancelled":
       * the response carries the subscription alone, and that sentence needs the period end from it
       * next to the plan name from the account.
       */
      cancelSubscription(onCancelled?: () => void): void {
        const orgKey = store.orgKey();
        if (!orgKey || store.saving()) return;
        patchState(store, { saving: true });
        service
          .cancelSubscription(orgKey)
          .pipe(
            tap(() => {
              patchState(store, { saving: false });
              load(orgKey);
              onCancelled?.();
            }),
            catchError(() => {
              patchState(store, { saving: false });
              return EMPTY;
            }),
          )
          .subscribe();
      },

      resumeSubscription(onResumed?: () => void): void {
        const orgKey = store.orgKey();
        if (!orgKey || store.saving()) return;
        patchState(store, { saving: true });
        service
          .resumeSubscription(orgKey)
          .pipe(
            tap(() => {
              patchState(store, { saving: false });
              load(orgKey);
              onResumed?.();
            }),
            catchError(() => {
              patchState(store, { saving: false });
              return EMPTY;
            }),
          )
          .subscribe();
      },
    };
  }),
  withDevtools('CustomerAccount'),
);
